import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";

export default function SyncFromWebsite({ onComplete }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const pollRef = useRef(null);

  // Load any previous sync state on mount so the admin sees the last result.
  useEffect(() => {
    let alive = true;
    api.get("/admin/sync-status")
      .then(({ data }) => { if (alive && data?.status && data.status !== "idle") setResult(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Clear polling timer on unmount.
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const poll = useCallback(async function poll() {
    try {
      const { data } = await api.get("/admin/sync-status");
      setResult(data);
      if (data?.status === "running") {
        pollRef.current = setTimeout(poll, 4000);
      } else {
        setBusy(false);
        if (data?.status === "done") {
          toast.success(`Synced ${data.sessions_imported} sessions, ${data.speakers_imported} speakers`);
          onComplete?.();
        } else if (data?.status === "failed") {
          toast.error(`Sync failed: ${data.error || "unknown error"}`);
        }
      }
    } catch (err) {
      // Don't surface - polling will retry on the next tick.
      pollRef.current = setTimeout(poll, 6000);
    }
  }, [onComplete]);

  const sync = async () => {
    if (!window.confirm("This will replace ALL sessions and speakers with what's currently on dftb26.com. Delegates' starred-agenda picks will be cleared because session IDs change. Continue?")) return;
    setBusy(true);
    try {
      await api.post("/admin/sync-from-dftb26");
      toast.message("Sync started - pulling Day 1/2/3 from dftb26.com. This usually takes ~90 seconds.");
      poll();
    } catch (err) {
      setBusy(false);
      toast.error(formatApiError(err));
    }
  };

  const isRunning = busy || result?.status === "running";

  return (
    <div className="mb-5 p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex flex-col sm:flex-row sm:items-center gap-3" data-testid="sync-website-card">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <ArrowsClockwise size={16} weight="duotone" className={`text-[#000000] ${isRunning ? "animate-spin" : ""}`} />
          <h3 className="font-display text-base">Sync from dftb26.com</h3>
        </div>
        <p className="text-xs text-[#5a5a5a]">
          Re-pulls Day 1/2/3 from the public website and replaces the entire programme + speaker list. Useful when you&apos;ve edited the schedule on the WordPress site and want delegates to see it immediately.
        </p>
        {result?.status === "running" && (
          <div className="text-xs text-[#000000] mt-2 font-mono-time" data-testid="sync-running-pill">
            Sync in progress… started {new Date(result.started_at).toLocaleTimeString()}
          </div>
        )}
        {result?.status === "done" && (
          <div className="text-xs text-[#7A828C] mt-2 font-mono-time" data-testid="sync-last-result">
            Last sync: {new Date(result.finished_at || result.synced_at).toLocaleString()} · {result.sessions_imported} sessions / {result.speakers_imported} speakers
            {result.errors?.length > 0 && <div className="text-[#DC4F48] mt-1">{result.errors.length} warning(s)</div>}
          </div>
        )}
        {result?.status === "failed" && (
          <div className="text-xs text-[#DC4F48] mt-2 font-mono-time" data-testid="sync-failed">
            Last sync failed: {result.error || "unknown error"}
          </div>
        )}
      </div>
      <Button onClick={sync} disabled={isRunning} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] sm:flex-shrink-0" data-testid="sync-website-btn">
        {isRunning ? "Syncing…" : "Pull latest"}
      </Button>
    </div>
  );
}
